/**
 * Planting plan flow steps (handoff §2–3.3).
 * Strip shows five labels; `reveal` is a post-wizard interstitial (not in the strip).
 */

export const PLANTING_FLOW_STEPS = Object.freeze([
  { id: 'ground', num: '01', label: 'GROUND' },
  { id: 'wizard', num: '02', label: 'WIZARD' },
  { id: 'plan', num: '03', label: 'PLAN' },
  { id: 'timing', num: '04', label: 'TIMING' },
  { id: 'growth', num: '05', label: 'GROWTH' },
]);

export const PLANTING_FLOW_STEP_IDS = Object.freeze(
  PLANTING_FLOW_STEPS.map((step) => step.id)
);

/** Screens that exist in the flow (includes reveal). */
export const PLANTING_FLOW_SCREEN_IDS = Object.freeze([
  'ground',
  'wizard',
  'reveal',
  'plan',
  'timing',
  'growth',
]);

export const DEFAULT_PLANTING_FLOW_STEP = 'ground';

export function normalizePlantingFlowStep(step) {
  const value = String(step || '').trim().toLowerCase();
  if (PLANTING_FLOW_SCREEN_IDS.includes(value)) return value;
  return DEFAULT_PLANTING_FLOW_STEP;
}

/** Which strip step is active for a given screen (reveal highlights wizard). */
export function stripStepIdForScreen(screen) {
  const normalized = normalizePlantingFlowStep(screen);
  if (normalized === 'reveal') return 'wizard';
  return normalized;
}

export function nextPlantingFlowStep(screen) {
  const normalized = normalizePlantingFlowStep(screen);
  const order = ['ground', 'wizard', 'reveal', 'plan', 'timing', 'growth'];
  const index = order.indexOf(normalized);
  if (index < 0 || index >= order.length - 1) return null;
  return order[index + 1];
}

export function previousPlantingFlowStep(screen) {
  const normalized = normalizePlantingFlowStep(screen);
  const order = ['ground', 'wizard', 'reveal', 'plan', 'timing', 'growth'];
  const index = order.indexOf(normalized);
  if (index <= 0) return null;
  return order[index - 1];
}

export function plantingFlowKicker(screen, {
  acresLabel = '',
  zoneLabel = '',
  wizardQuestion = 0,
  planName = '',
  windowLabel = '',
  plantShort = '',
  viewing = false,
} = {}) {
  let body = 'PLANTING ASSISTANT';
  switch (normalizePlantingFlowStep(screen)) {
    case 'ground':
      body = 'NEW PLAN · STEP 1 OF 5';
      break;
    case 'wizard': {
      const n = Math.min(Math.max(Number(wizardQuestion) || 1, 1), 5);
      body = `QUESTION ${n} OF 5`;
      break;
    }
    case 'reveal':
      body = zoneLabel
        ? `READING ${zoneLabel}`
        : 'READING YOUR GROUND';
      break;
    case 'plan': {
      const name = String(planName || '').trim().toUpperCase();
      if (name && acresLabel) body = `${name} · ${acresLabel}`;
      else if (acresLabel) body = `${acresLabel} · BLEND`;
      else body = 'BUILD THE BLEND';
      break;
    }
    case 'timing': {
      if (!windowLabel) {
        body = 'FALL WINDOW · TIMING';
        break;
      }
      const isSpring = /MAR|APR|MAY/i.test(String(windowLabel));
      body = `${isSpring ? 'SPRING' : 'FALL'} WINDOW · ${String(windowLabel).toUpperCase()}`;
      break;
    }
    case 'growth': {
      if (acresLabel && plantShort) {
        body = `${acresLabel} · ${String(plantShort).toUpperCase()}`;
      } else {
        body = acresLabel ? `${acresLabel} · GROWTH` : 'TYPICAL GROWTH';
      }
      break;
    }
    default:
      break;
  }
  return viewing ? `VIEWING · ${body}` : body;
}

/**
 * Field Edition blend step vs legacy seed calculator.
 * Compose is always Field Edition. Saved Field Edition plans have a season
 * and/or no map polygons (acres + town). Legacy groups keep the old calculator.
 */
export function usesFieldEditionPlanStep({
  calculatorMode,
  plantingSeason,
  selectedPolygonCount = 0,
} = {}) {
  if (calculatorMode === 'compose') return true;
  const season = String(plantingSeason || '').toLowerCase();
  if (season === 'fall' || season === 'spring') return true;
  return Number(selectedPolygonCount) <= 0;
}

export function continueLabelForStep(screen, { wizardLabel, revealLabel, growthLabel, planLabel, viewing } = {}) {
  if (viewing) return 'EDIT THIS PLAN';
  switch (normalizePlantingFlowStep(screen)) {
    case 'ground':
      return 'CONTINUE TO WIZARD';
    case 'wizard':
      return wizardLabel || 'READ THE GROUND';
    case 'reveal':
      return revealLabel || 'SEE THE PLAN';
    case 'plan':
      return planLabel || 'PICK PLANTING DATES';
    case 'timing':
      return 'SEE THE GROWTH TIMELINE';
    case 'growth':
      return growthLabel || 'SAVE THIS PLAN';
    default:
      return 'CONTINUE';
  }
}
